const ALLOWED_TAGS = new Set([
  'A', 'B', 'BLOCKQUOTE', 'BR', 'CODE', 'DIV', 'EM', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'HR', 'I', 'LI', 'OL', 'P', 'PRE', 'S', 'SPAN', 'STRONG', 'TABLE', 'TBODY', 'TD',
  'TH', 'THEAD', 'TR', 'U', 'UL',
]);

const BLOCKED_TAGS = new Set([
  'BASE', 'BUTTON', 'CANVAS', 'EMBED', 'FORM', 'IFRAME', 'INPUT', 'LINK', 'MATH', 'META',
  'NOSCRIPT', 'OBJECT', 'OPTION', 'SCRIPT', 'SELECT', 'STYLE', 'SVG', 'TEXTAREA', 'VIDEO',
]);

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const createDocument = (value) => {
  if (typeof DOMParser === 'undefined') return null;
  const documentNode = new DOMParser().parseFromString('<!doctype html><html><body></body></html>', 'text/html');
  const root = documentNode.createElement('div');
  root.setAttribute('data-notification-root', 'true');
  root.innerHTML = value;
  documentNode.body.appendChild(root);
  return documentNode;
};

const isSafeHref = (value) => {
  const href = String(value || '').trim();
  return href.startsWith('#')
    || href.startsWith('/')
    || href.startsWith('./')
    || href.startsWith('../')
    || /^(https?:|mailto:|tel:)/i.test(href);
};

const preserveTextLineBreaks = (root, documentNode) => {
  const walker = documentNode.createTreeWalker(root, 4);
  const textNodes = [];

  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((textNode) => {
    if (textNode.parentElement?.closest('pre')) return;

    const text = String(textNode.nodeValue || '').replace(/\r\n?/g, '\n');
    if (!text.includes('\n') || !text.trim()) return;

    const fragment = documentNode.createDocumentFragment();
    text.split('\n').forEach((line, index) => {
      if (index > 0) fragment.appendChild(documentNode.createElement('br'));
      if (line) fragment.appendChild(documentNode.createTextNode(line));
    });
    textNode.replaceWith(fragment);
  });
};

export const sanitizeNotificationHtml = (value) => {
  const rawHtml = String(value || '');
  if (!rawHtml) return '';

  const documentNode = createDocument(rawHtml);
  if (!documentNode) return escapeHtml(rawHtml).replace(/\r?\n/g, '<br>');

  const root = documentNode.body.querySelector('[data-notification-root]');
  if (!root) return '';

  Array.from(root.querySelectorAll('*')).forEach((element) => {
    const tagName = element.tagName.toUpperCase();

    if (BLOCKED_TAGS.has(tagName)) {
      element.remove();
      return;
    }

    if (!ALLOWED_TAGS.has(tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }

    const originalHref = element.getAttribute('href');
    const originalTitle = element.getAttribute('title');
    Array.from(element.attributes).forEach((attribute) => element.removeAttribute(attribute.name));

    if (tagName === 'A') {
      if (isSafeHref(originalHref)) {
        element.setAttribute('href', originalHref.trim());
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noopener noreferrer nofollow');
      }
      if (originalTitle) element.setAttribute('title', originalTitle);
    }
  });

  preserveTextLineBreaks(root, documentNode);

  return root.innerHTML;
};

export const notificationPlainText = (value) => {
  const rawHtml = String(value || '');
  if (!rawHtml) return '';
  const documentNode = createDocument(sanitizeNotificationHtml(rawHtml));
  const root = documentNode?.body.querySelector('[data-notification-root]');
  root?.querySelectorAll('blockquote, br, div, h1, h2, h3, h4, h5, h6, hr, li, ol, p, pre, table, td, th, tr, ul')
    .forEach((element) => element.insertAdjacentText('afterend', ' '));
  const text = root?.textContent || rawHtml.replace(/<[^>]*>/g, ' ');
  return text.replace(/\s+/g, ' ').trim();
};
